import { autoinject, computedFrom, transient } from "aurelia-framework";
import { I18N } from "aurelia-i18n";
import { ValidationController, ValidationRules } from "aurelia-validation";
import { CustomValidationRenderer } from "../../../../../../client-validation-system/custom-validation-renderer";
import { RightShipDialogServiceConstants } from "../../../../../../constants/basics/rightship-dialog.constants";
import { LookupValue } from "../../../../../../models/lookups/lookup.models";
import { ChangeRSInspectionInspectorRequest, RightShipInspectionClientModel, RightShipInspectionType } from "../../../../../../models/rightship-inspections/rightship-inspections.models";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { NotificationClientService, NotificationType } from "../../../../../../services/basics/notification-client.service";
import { RightShipInspectionsService } from "../../../../../../services/rightship-inspections/rightship-inspections.service";
import { ValidationsClientService } from "../../../../../../services/validations/validations-client.service";
import { CloneHelper } from "@rightship-tech/common-ui/helpers/clone.helper";
import { EmailValidator } from "../../../../../../utilities/email-validator";

@autoinject
@transient()
export class ChangeRightshipInspectionInspectorDialog 
{
    originalInspectionData: { inspectorId: number, inspector2Id: number } = { inspectorId: null, inspector2Id: null};
    inspection: RightShipInspectionClientModel;
    onSave: () => void;
    private _inspectionParameters:string ="";

    constructor(private dialogClientService: DialogClientService, private rightshipInspectionService: RightShipInspectionsService,
        private i18N: I18N, private notificationService: NotificationClientService,
        private validationsClientService: ValidationsClientService,
        private validationController: ValidationController) {

        this.validationController.addRenderer(new CustomValidationRenderer());
    }

    async activate(model: { item: RightShipInspectionClientModel, onSave: () => void }) {
        if (!model) return;
        Object.assign(this.originalInspectionData, { inspectorId: model.item.inspectorId, inspector2Id: model.item.inspector2Id });
        this.inspection = CloneHelper.cloneObject(model.item);
        this.onSave = model.onSave;
        this.addValidationRules();
        
        this._inspectionParameters = this.inspection.inspectingCompanyId.toString();
        const filterByAccredited = true;
        const filterOutSuspended = true;
        if(this.inspection.useMobileInspectionWorkflow)
            this._inspectionParameters += "," + filterByAccredited;
        else
            this._inspectionParameters += ",null," + filterOutSuspended;
    }

    @computedFrom("_inspectionParameters")
    get inspectionParameters(){
        return this._inspectionParameters
    }


    addValidationRules() {
        ValidationRules
            .ensure<RightShipInspectionClientModel, string>(x => x.inspectorEmail)
            .displayName('Inspector 1 Email')
            .satisfies(x => EmailValidator.isValidEmail(x))
            .ensure<string>(x => x.inspector2Email)
            .displayName('Inspector 2 Email')
            .satisfies((x, insp) => insp.inspector2Id && insp.inspector2Id > 0 ? EmailValidator.isValidEmail(x) : true)
            .ensure(x => x.inspectorId)
            .required()
            .when(item => item.inspector2Id > 0)
            .withMessage("Cannot have inspector 2 assigned when inspector 1 has not been assigned")
            .on(this.inspection);
    }

    @computedFrom('inspection', 'inspection.inspectionTypeId')
    get isWet() {
        return this.inspection && this.inspection.inspectionTypeId == RightShipInspectionType.TankerInspection;
    }

    @computedFrom('inspection', 'inspection.inspectionTypeId')
    get isCargill() {
        return this.inspection && this.inspection.inspectionTypeId == RightShipInspectionType.CargillSurvey;
    }

    close() {
        this.dialogClientService.hide();
    }    

    async submit()
    {
        try {
            let validationResponse = await this.validationController.validate({object: this.inspection});

            if (!validationResponse.valid) return;

            validationResponse = await this.validationController.validate({object: this});

            if (!validationResponse.valid) 
            {
                const validationMessage = validationResponse.results.filter(x => x.valid == false && x.message).join("\n");
                this.notificationService.show(this.i18N.tr('error-text'), validationMessage, NotificationType.Error);
                return;
            }

            const save = async () => {
                const response = await this.rightshipInspectionService.changeRSInspectionInspector(<ChangeRSInspectionInspectorRequest>{
                    rightShipInspectionId: this.inspection.rightShipInspectionId,
                    inspectorId: this.inspection.inspectorId,
                    inspectorEmail: this.inspection.inspectorEmail,
                    inspectorContactNumber: this.inspection.inspectorContactNumber,
                    inspector2Id: this.inspection.inspector2Id,
                    inspector2Email: this.inspection.inspector2Email,
                    inspector2ContactNumber: this.inspection.inspector2ContactNumber
                });
    
                if (response.validationResult) {
                    this.validationsClientService.showValidationErrors(response.validationResult);
                    return;
                }
    
                if(this.onSave) this.onSave();
                
                this.notificationService.show(this.i18N.tr('success-text'), "", NotificationType.Success);
    
                this.close();
            };

            if(this.shouldShowConfirmation())
            {
                this.showConfirmationDialog(save);
            }
            else
            {
                await save();
            }

            
        }
        catch (e)
        {
            console.error(e);
            this.notificationService.show(this.i18N.tr('error-text'), "Failed to change inspector", NotificationType.Error);
        }
    }

    inspectorChanged(columnPrefix, item, oldItem)
    {
        this.debounce(() => {
            const inspectorChanged = item && (!oldItem || (oldItem && item.id != oldItem.id));
            if (!inspectorChanged) return;

            this.inspection[columnPrefix + 'ContactNumber'] = item && item.additionalData && item.additionalData.mobileNumber != null? item.additionalData.mobileNumber: 
                (!oldItem? this.inspection[columnPrefix + 'ContactNumber']: null);
            this.inspection[columnPrefix + 'Email'] = item && item.additionalData && item.additionalData.email != null? item.additionalData.email: 
                (!oldItem? this.inspection[columnPrefix + 'Email']: null);
        }, 100);
    }

    private debounceTimeout;
    debounce = (func: (...args) => any, wait: number = 100, immediate: boolean = false, ...args) =>  {

        const later = function (...args) {
            this.debounceTimeout = null;
            if (!immediate) func.apply(this, args);
        };

        clearTimeout(this.debounceTimeout);

        this.debounceTimeout = setTimeout(later, wait);

        if (immediate) func.apply(this, args);
    }

    inspectorDisplayName: (item: LookupValue) => string = (item: LookupValue) => {
        if(item)
        {
            return item.additionalData && item.additionalData.accreditationStatus? `${item.name} - ${item.additionalData.accreditationStatus}` : item.name;
        }
        
        return "";
    }

    shouldShowConfirmation(): boolean
    {
        const tabletSynced = this.inspection && this.inspection.canForceTabletSync &&  this.inspection.tabletSyncStatus.toLowerCase() == 'synced';

        const inspectorsChanged = this.originalInspectionData.inspectorId != this.inspection.inspectorId || this.originalInspectionData.inspector2Id != this.inspection.inspector2Id;

        return tabletSynced && inspectorsChanged;
    }

    showConfirmationDialog(onProceed: () => Promise<void>)
    {
        const model = { onProceed: async () => {
            await onProceed();
        }, onCancel: () => { } };
        
        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.ChangeInspectorConfirmationDialog, model, false);
    }

    @computedFrom("inspection", "inspection.inspector2Id")
    get excludedInspector1Ids()
    {
        return [ this.inspection.inspector2Id ];
    }
    
    @computedFrom("inspection", "inspection.inspectorId")
    get excludedInspector2Ids()
    {
        return [ this.inspection.inspectorId ];
    }
}